import { useQuery } from "@tanstack/react-query";
import { CheckCircle, HistoryIcon, ScanText } from "lucide-react";
import { useMemo, useState } from "react";
import { LoadingState } from "./LoadingState";
import { ScanResults } from "./ScanResults";
import { ScoreGauge } from "./ScoreGauge";
import aiApi from "@/api/ai";
import { Alert } from "@/components/Alert/Alert";
import { Button } from "@/components/Button/Button";
import { SideBarHeader } from "@/components/Sidebar/SideBarHeader";
import { AppText } from "@/components/Text/AppText";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { useInvalidateUserFeaturesCache } from "@/hooks/useAppData";
import { useTransaction } from "@/hooks/useTransaction";
import { openModal } from "@/store/modal/slice";
import { getActiveResumeId } from "@/store/resume/selectors";
import { getUserAtsScansRemaining } from "@/store/user/selectors";
import { CreditActionCode } from "@/types/creditAction";
import { ModalTypes } from "@/types/modal";
import { pluralise } from "@/utils/string";
import { trackPostHogEvent } from "@/utils/tracking";

export const SidebarAtsScan = () => {
    const activeResumeId = useAppSelector(getActiveResumeId);
    const [isScanning, setIsScanning] = useState(false);
    const [currentScore, setCurrentScore] = useState<number>(0);
    const dispatch = useAppDispatch();
    const { validateCreditAction } = useTransaction();
    const [scanError, setScanError] = useState<string | null>(null);
    const invalidateUserFeaturesCache = useInvalidateUserFeaturesCache();
    const scansRemaining = useAppSelector(getUserAtsScansRemaining);
    const trueScansRemaining = useMemo(
        () => (scansRemaining === undefined ? 5 : scansRemaining),
        [scansRemaining],
    );

    const {
        data: scans,
        isLoading: isLoadingScans,
        refetch: refetchScans,
    } = useQuery({
        queryKey: ["resumes-ats-scans", activeResumeId],
        queryFn: () => aiApi.getResumesAtsScanResults(activeResumeId!),
        enabled: !!activeResumeId,
    });
    const mostRecentScan = scans && scans.length > 0 ? scans[0] : null;

    const openScanHistory = () => {
        if (!activeResumeId) return;
        dispatch(
            openModal({
                modalType: ModalTypes.ATS_SCAN_HISTORY,
                props: {
                    scans: scans ?? [],
                },
            }),
        );
    };

    const onScan = async () => {
        if (!activeResumeId) return;

        trackPostHogEvent("ats_scan", {
            feature_name: "ats_scan",
            action_taken: "start_scan_clicked",
        });

        setIsScanning(true);
        setScanError(null);
        try {
            const { creditActionError, hasInsufficientFunds } =
                validateCreditAction({
                    actionCode: CreditActionCode.ATS_SCAN,
                });
            if (creditActionError || hasInsufficientFunds) {
                setScanError(creditActionError || "Insufficient credits");
                return;
            }
            const response = await aiApi.scanResume(
                {
                    resume: activeResumeId,
                },
                dispatch,
            );
            setCurrentScore(response.score);
            trackPostHogEvent("ats_scan", {
                action_taken: "scan_completed",
                score: response.score,
            });
            await refetchScans();
            invalidateUserFeaturesCache();
        } catch (error) {
            console.error("Scan failed:", error);
            setScanError("An error occurred while scanning your resume");
        } finally {
            setIsScanning(false);
        }
    };

    return (
        <div className="relative flex h-full flex-col">
            <SideBarHeader
                isBeta
                title="ATS Scanner"
                betaMessage="Beta feature – we’re continuously refining and enhancing accuracy for better results."
            />
            <div className="no-scrollbar h-full overflow-y-scroll pb-10">
                <div className="mx-4">
                    <AppText
                        variant="regular"
                        className="mb-4 text-neutral-700"
                    >
                        Get tailored ATS insights by scanning your resume for
                        keywords, formatting, and structure. Our AI evaluates
                        your resume across multiple categories, providing
                        detailed scores and actionable feedback.
                    </AppText>

                    <div className="mb-6 flex justify-center">
                        <ScoreGauge
                            score={
                                isScanning
                                    ? 0
                                    : currentScore > 0
                                      ? currentScore
                                      : (mostRecentScan?.score ?? 0)
                            }
                            loading={isScanning}
                        />
                    </div>

                    <div className="mb-4 flex justify-center">
                        {trueScansRemaining === 0 ? (
                            <AppText
                                variant="footnote"
                                className="!font-semibold text-error-400"
                            >
                                You have reached your daily limit of 5 free
                                scans
                            </AppText>
                        ) : (
                            <AppText
                                variant="footnote"
                                className="!font-semibold text-neutral-500"
                            >
                                {trueScansRemaining} FREE{" "}
                                {pluralise(trueScansRemaining, "SCAN", "SCANS")}{" "}
                                REMAINING TODAY
                            </AppText>
                        )}
                    </div>

                    {scans && scans.length === 0 && (
                        <div className="mb-6 flex flex-col items-center gap-2">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-primary-700" />
                                <AppText
                                    variant="regular"
                                    className="text-neutral-700"
                                >
                                    Resume structure & content
                                </AppText>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-primary-700" />
                                <AppText
                                    variant="regular"
                                    className="text-neutral-700"
                                >
                                    Formatting & readability
                                </AppText>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-primary-700" />
                                <AppText
                                    variant="regular"
                                    className="text-neutral-700"
                                >
                                    ATS-friendly formatting
                                </AppText>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-primary-700" />
                                <AppText
                                    variant="regular"
                                    className="text-neutral-700"
                                >
                                    Writing quality & style
                                </AppText>
                            </div>
                        </div>
                    )}

                    {scanError && (
                        <Alert
                            className="mb-4"
                            type="error"
                            message={scanError}
                            dismissable={true}
                        />
                    )}

                    {/* Start Scan Button */}
                    <div className="mb-6 flex items-center justify-center gap-2">
                        <Button
                            onClick={onScan}
                            disabled={
                                !activeResumeId ||
                                isScanning ||
                                trueScansRemaining === 0
                            }
                            leftIcon={<ScanText className="h-4 w-4" />}
                        >
                            Start Scan
                        </Button>
                        {/* Scan History - only if more than 1 scan */}
                        {scans && scans.length > 1 && (
                            <Button
                                onClick={openScanHistory}
                                color="grey"
                                leftIcon={<HistoryIcon className="h-4 w-4" />}
                            >
                                Scan History
                            </Button>
                        )}
                    </div>

                    {isLoadingScans && <LoadingState />}
                </div>

                <ScanResults
                    scanResults={mostRecentScan?.scanResults}
                    scanDate={
                        mostRecentScan?.createdAt
                            ? new Date(
                                  mostRecentScan.createdAt,
                              ).toLocaleDateString()
                            : undefined
                    }
                    isScanning={isScanning}
                    title="Recommendations"
                />
            </div>
        </div>
    );
};
